import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import i18n from '@risksmart-app/i18n/src/i18n';
import {
  useGetThirdPartyRegister,
  type UseGetThirdPartyRegisterArgs,
} from 'src/hooks/queries/third-party/useGetThirdPartyRegister';

import { thirdPartyRegisterUrl } from '@/utils/urls';

import { useGetThirdPartySmartWidgetTableProps } from '../../../third-party/config';
import type { ThirdPartyRegisterFields } from '../../../third-party/types';
import { UNRATED } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

type GetThirdPartiesData = NonNullable<
  ReturnType<typeof useGetThirdPartyRegister>['data']
>;

export default createDataSource<
  ThirdPartyRegisterFields,
  UseGetThirdPartyRegisterArgs,
  GetThirdPartiesData
>({
  group: 'single',
  parentTypes: [Parent_Type_Enum.ThirdParty],
  hasAccess: (_, isModuleEnabled) => isModuleEnabled('third_party'),
  useWidgetQuery: useGetThirdPartyRegister,
  useTablePropsHook: (data, options) =>
    useGetThirdPartySmartWidgetTableProps(data?.third_party, options),
  entityNamePlural: 'third_party_other',
  entityNameSingular: 'third_party_one',
  fields: 'third_party.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'CreatedAtTimestamp',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
  },
  clickThroughUrl: (filter) => thirdPartyRegisterUrl(filter),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<ThirdPartyRegisterFields>(),
    {
      id: 'type',
      fieldId: 'type',
      name: () => i18n.t('columns.type'),
      categoryGetter: (item) => ({
        key: item.Type ?? UNRATED,
        label: item.TypeLabelled ?? UNRATED,
      }),
      ratingColourKey: 'third_party_type',
      clickthroughFilter: defaultClickthroughFilterWithUnrated('TypeLabelled'),
    },
    {
      id: 'status',
      fieldId: 'status',
      name: () => i18n.t('columns.status'),
      categoryGetter: (item) => ({
        key: item.Status ?? UNRATED,
        label: item.StatusLabelled ?? UNRATED,
      }),
      ratingColourKey: 'third_party_status',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
    },
    {
      id: 'criticality',
      fieldId: 'criticality',
      name: () => i18n.t('third_party.columns.criticality'),
      categoryGetter: (item) => ({
        key: item.Criticality ?? UNRATED,
        label: item.CriticalityLabelled ?? UNRATED,
        sortKey: `${item.Criticality}`,
      }),
      ratingColourKey: 'third_party_criticality',
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'CriticalityLabelled'
      ),
    },
    {
      id: 'owner',
      fieldId: 'Owners',
      name: () => i18n.t('columns.owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      fieldId: 'Contributors',
      name: () => i18n.t(`columns.contributor`),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
  ],
});
